/**
 * How much the data behind a figure took to read: "15,103 rows read · 0.006 s".
 *
 * Fed by the optional `query_stats` the server puts on read responses. It
 * renders nothing when a response has none, so an older server, a cached
 * response or an endpoint that read nothing simply shows no chip.
 *
 * "Rows read" is what was scanned, not what came back, so it can be larger
 * than the result count beside it. The tooltip says so rather than leaving
 * the bigger number to look like a mistake.
 */
import { Box, Group, Text, Tooltip } from '@mantine/core';
import type { CSSProperties } from 'react';
import type { components } from '../api/schema';
import { FONTS, formatNumber } from '../theme';

export type QueryStats = components['schemas']['QueryStats'];

/** Seconds, to the millisecond while it is small, coarser once it is not. */
export function formatSeconds(ms: number): string {
  const s = Math.max(0, ms) / 1000;
  if (s < 10) return `${s.toFixed(3)} s`;
  if (s < 100) return `${s.toFixed(1)} s`;
  return `${Math.round(s)} s`;
}

export function speedLabel(stats: QueryStats): string {
  const rows = stats.rows_read === 1 ? 'row read' : 'rows read';
  return `${formatNumber(stats.rows_read)} ${rows} · ${formatSeconds(stats.elapsed_ms)}`;
}

export function SpeedChip({
  stats,
  style,
}: {
  stats?: QueryStats | null;
  style?: CSSProperties;
}) {
  if (!stats) return null;
  const detail =
    `${formatNumber(stats.rows_read)} rows scanned to answer this, ` +
    `in ${stats.queries === 1 ? 'one read' : `${stats.queries} reads`}. ` +
    'Reads that ran side by side count once in the time.';
  return (
    <Tooltip label={detail} multiline w={260} withArrow openDelay={300}>
      <Group gap={6} wrap="nowrap" style={{ display: 'inline-flex', ...style }} data-testid="speed-chip">
        <Box
          aria-hidden
          w={6}
          h={6}
          style={{ borderRadius: '50%', background: 'var(--mantine-color-chartGreen-6)', flexShrink: 0 }}
        />
        <Text
          component="span"
          c="dimmed"
          style={{ fontFamily: FONTS.mono, fontSize: '0.6875rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}
        >
          {speedLabel(stats)}
        </Text>
      </Group>
    </Tooltip>
  );
}
